import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChatFlowFormFillRound, ChatFlowProfileSectionItem } from '../../../../services/chatFlowApi';
import editIcon from '../../../../assests/icons/edit_icon.png';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import commentIcon from '../../../../assests/icons/comment.png';
import ContinueBlock from '../../submitDocuments/components/ContinueBlock';
import type { FillingFormProgressStatus, FillingFormsChecklistItem } from './types';
import './FillingFormsChecklistScreen.css';

export interface FillingFormsChecklistForm {
  formCode: string;
  name: string;
  nameJa?: string;
  status?: FillingFormProgressStatus;
  adminApproved?: boolean | null;
  adminComment?: string | null;
  roundsBySubmission?: ChatFlowFormFillRound[];
  /** False when the invitation includes this form and the employee has not started it. */
  choiceRecorded?: boolean;
  applicable?: boolean | null;
  careerUpRole?: 'confirmation' | 'application' | null;
}

/** Circled number ① ② ③ … (Unicode) — same as various copies list */
function circledNumber(index: number): string {
  if (index >= 0 && index < 20) return String.fromCodePoint(0x2460 + index);
  return String(index + 1);
}

function hadRejectionRound(form: FillingFormsChecklistForm): boolean {
  return (form.roundsBySubmission ?? []).some((r) => r.adminApproved === false);
}

function showFormReviewEditActions(form: FillingFormsChecklistForm): boolean {
  if (form.adminApproved === true) return false;
  if (form.adminApproved === false || hadRejectionRound(form)) return true;
  return form.status === 'completed' || form.status === 'final_confirm';
}

export interface FillingFormsChecklistScreenProps {
  onBack: () => void;
  /** choices = first-time checklist; review = resubmission review (approve/reject + comment + fix). */
  mode?: 'choices' | 'review';
  /** fill-in includes 基本情報/家族情報; confirmation is agree-only forms. */
  section?: 'fill-in' | 'confirmation';
  forms?: FillingFormsChecklistForm[];
  formsCountHint?: number;
  generalInfoStatus: FillingFormProgressStatus;
  onGeneralInfoStatusChange: (status: FillingFormProgressStatus) => void;
  familyInfoStatus: FillingFormProgressStatus;
  onFamilyInfoStatusChange: (status: FillingFormProgressStatus) => void;
  formStatuses: Record<string, FillingFormProgressStatus>;
  onFormStatusChange: (formCode: string, status: FillingFormProgressStatus) => void;
  onOpenGeneralInfoChat: () => void;
  onOpenFamilyInfoChat: () => void;
  onEditChecklistItem?: (item: FillingFormsChecklistItem) => void | Promise<void>;
  onBeforeStartFormItem?: (item: FillingFormsChecklistItem) => Promise<boolean>;
  onStartItem?: (item: FillingFormsChecklistItem) => void;
  /** Admin review state for 基本情報 / 家族情報 (from chat-flow profileSections). */
  profileSections?: ChatFlowProfileSectionItem[];
  /** Fix a rejected row in review mode (forms + profile sections). */
  onFixRejectedForm?: (item: FillingFormsChecklistItem) => void | Promise<void>;
}

function statusLabel(t: (key: string) => string, status: FillingFormProgressStatus) {
  if (status === 'completed') return t('submitDocuments.fillFormsChecklist.status.completed');
  if (status === 'final_confirm') return t('submitDocuments.fillFormsChecklist.status.completed');
  if (status === 'in_progress') return t('submitDocuments.fillFormsChecklist.status.inProgress');
  return t('submitDocuments.fillFormsChecklist.status.notYet');
}

const FillingFormsChecklistScreen: React.FC<FillingFormsChecklistScreenProps> = ({
  onBack,
  mode = 'choices',
  section = 'fill-in',
  forms,
  formsCountHint,
  generalInfoStatus,
  onGeneralInfoStatusChange,
  familyInfoStatus,
  onFamilyInfoStatusChange,
  formStatuses,
  onFormStatusChange,
  onOpenGeneralInfoChat,
  onOpenFamilyInfoChat,
  onEditChecklistItem,
  onBeforeStartFormItem,
  onStartItem,
  onFixRejectedForm,
  profileSections,
}) => {
  const { t } = useLanguage();
  const [commentForm, setCommentForm] = useState<FillingFormsChecklistItem | null>(null);
  const isConfirmationSection = section === 'confirmation';

  const effectiveForms: FillingFormsChecklistForm[] = useMemo(() => {
    if (forms && forms.length > 0) return forms;
    const count = typeof formsCountHint === 'number' && formsCountHint > 0 ? formsCountHint : 0;
    return Array.from({ length: count }).map((_, idx) => {
      const n = idx + 1;
      const code = `FF${String(n).padStart(3, '0')}`;
      const prefix = t('submitDocuments.fillFormsChecklist.placeholderFormNamePrefix');
      return { formCode: code, name: `${prefix}${circledNumber(idx)}` };
    });
  }, [forms, formsCountHint, t]);

  const items: FillingFormsChecklistItem[] = useMemo(() => {
    const list: FillingFormsChecklistItem[] = [];
    if (!isConfirmationSection) {
      const generalReview = profileSections?.find((s) => s.section === 'general_info');
      const familyReview = profileSections?.find((s) => s.section === 'family_info');
      list.push(
        {
          kind: 'generalInfo',
          id: 'generalInfo',
          title: t('submitDocuments.fillFormsChecklist.generalInfo'),
          status: generalInfoStatus,
          adminApproved: generalReview?.adminApproved,
          adminComment: generalReview?.adminComment,
        },
        {
          kind: 'familyInfo',
          id: 'familyInfo',
          title: t('submitDocuments.fillFormsChecklist.familyInfo'),
          status: familyInfoStatus,
          adminApproved: familyReview?.adminApproved,
          adminComment: familyReview?.adminComment,
        }
      );
    }
    effectiveForms.forEach((f) => {
      list.push({
        kind: 'form',
        id: f.formCode,
        formCode: f.formCode,
        title: f.name,
        nameJa: f.nameJa,
        status: formStatuses[f.formCode] ?? f.status ?? 'notyet',
        adminApproved: f.adminApproved,
        adminComment: f.adminComment,
        roundsBySubmission: f.roundsBySubmission,
      });
    });
    return list;
  }, [
    effectiveForms,
    formStatuses,
    generalInfoStatus,
    familyInfoStatus,
    profileSections,
    isConfirmationSection,
    t,
  ]);

  const titleKey = isConfirmationSection
    ? 'submitDocuments.confirmationFormsChecklist.title'
    : 'submitDocuments.fillFormsChecklist.title';
  const introKey =
    mode === 'review'
      ? isConfirmationSection
        ? 'submitDocuments.confirmationFormsChecklist.reviewIntro'
        : 'submitDocuments.fillFormsChecklist.reviewIntro'
      : isConfirmationSection
        ? 'submitDocuments.confirmationFormsChecklist.intro'
        : 'submitDocuments.fillFormsChecklist.intro';

  const currentIndex = useMemo(() => {
    const idx = items.findIndex((i) => i.status !== 'completed' && i.status !== 'final_confirm');
    return idx >= 0 ? idx : items.length - 1;
  }, [items]);

  const handleStartOrResume = async (item: FillingFormsChecklistItem) => {
    if (item.kind === 'generalInfo') {
      onGeneralInfoStatusChange('in_progress');
      onOpenGeneralInfoChat();
    } else if (item.kind === 'familyInfo') {
      onFamilyInfoStatusChange('in_progress');
      onOpenFamilyInfoChat();
    } else {
      let ok = true;
      if (onBeforeStartFormItem) {
        ok = await onBeforeStartFormItem(item);
      }
      if (!ok) return;
      onFormStatusChange(item.formCode, 'in_progress');
    }
    onStartItem?.(item);
  };

  const handleEdit = async (item: FillingFormsChecklistItem) => {
    if (onEditChecklistItem) {
      await onEditChecklistItem(item);
      return;
    }
    if (item.kind === 'generalInfo') {
      onOpenGeneralInfoChat();
    } else if (item.kind === 'familyInfo') {
      onOpenFamilyInfoChat();
    } else {
      onFormStatusChange(item.formCode, 'in_progress');
      onStartItem?.(item);
    }
  };

  const handleReviewFix = async (item: FillingFormsChecklistItem) => {
    if (onFixRejectedForm) {
      await onFixRejectedForm(item);
      return;
    }
    if (item.kind === 'form') {
      if (item.status === 'in_progress' || item.status === 'notyet') {
        await handleStartOrResume(item);
        return;
      }
      await handleEdit(item);
      return;
    }
    await handleEdit(item);
  };

  const commentTitle =
    commentForm != null ? `${commentForm.title} — コメント` : 'コメント';

  return (
    <div className={`fill-forms-checklist${mode === 'review' ? ' fill-forms-checklist--review' : ''}`}>
      <div className="fill-forms-checklist__card">
        <div className="fill-forms-checklist__header">
          <h1 className="fill-forms-checklist__title">{t(titleKey)}</h1>
          <p className="fill-forms-checklist__intro">{t(introKey)}</p>
        </div>

        <div className="fill-forms-checklist__scroll">
          <ul className="fill-forms-checklist__list">
            {items.map((item, idx) => {
              if (
                mode === 'review' &&
                (item.kind === 'form' || item.kind === 'generalInfo' || item.kind === 'familyInfo')
              ) {
                const isRejected = item.adminApproved === false;
                const isApproved = item.adminApproved === true;
                const showFix =
                  item.kind === 'form'
                    ? showFormReviewEditActions({
                        formCode: item.formCode,
                        name: item.title,
                        nameJa: item.nameJa,
                        status: item.status,
                        adminApproved: item.adminApproved,
                        adminComment: item.adminComment,
                        roundsBySubmission: item.roundsBySubmission,
                      })
                    : item.adminApproved === false;
                const commentText = item.adminComment?.trim() ?? '';
                const canStart =
                  !showFix && (item.status === 'notyet' || item.status === 'in_progress');
                const startLabel =
                  item.status === 'in_progress'
                    ? t('submitDocuments.fillFormsChecklist.resume')
                    : t('submitDocuments.fillFormsChecklist.start');

                return (
                  <li key={item.id} className="fill-forms-checklist__block">
                    <div className="fill-forms-checklist__block-inner">
                      <div className="fill-forms-checklist__block-title-bar">
                        <span className="fill-forms-checklist__block-num" aria-hidden>
                          {circledNumber(idx)}
                        </span>
                        <span className="fill-forms-checklist__block-title">{item.title}</span>
                        <span className="fill-forms-checklist__review-icons" aria-hidden>
                          {isApproved && (
                            <img src={approveIcon} alt="" className="fill-forms-checklist__review-icon" />
                          )}
                          {isRejected && (
                            <img src={rejectIcon} alt="" className="fill-forms-checklist__review-icon" />
                          )}
                        </span>
                      </div>
                      <div className="fill-forms-checklist__meta">
                        <span className="fill-forms-checklist__status">
                          {statusLabel(t, item.status)}
                        </span>
                      </div>
                    </div>
                    <div
                      className="fill-forms-checklist__review-actions"
                      aria-hidden={!showFix && !canStart}
                    >
                      {showFix && (
                        <>
                          {commentText !== '' && (
                            <button
                              type="button"
                              className="fill-forms-checklist__review-action-btn"
                              aria-label="コメントを見る"
                              onClick={() => setCommentForm(item)}
                            >
                              <img src={commentIcon} alt="" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="fill-forms-checklist__review-action-btn"
                            aria-label="修正する"
                            onClick={() => void handleReviewFix(item)}
                          >
                            <img src={editIcon} alt="" />
                          </button>
                        </>
                      )}
                      {canStart && (
                        <button
                          type="button"
                          className="fill-forms-checklist__block-start"
                          onClick={() => void handleStartOrResume(item)}
                        >
                          {startLabel}
                        </button>
                      )}
                    </div>
                  </li>
                );
              }

              const isDone = item.status === 'completed' || item.status === 'final_confirm';
              const isCurrent = idx === currentIndex && !isDone;
              const showStart = isCurrent && (item.status === 'notyet' || item.status === 'in_progress');
              const startLabel =
                item.status === 'in_progress'
                  ? t('submitDocuments.fillFormsChecklist.resume')
                  : t('submitDocuments.fillFormsChecklist.start');
              const showEdit = isDone;

              return (
                <li key={item.id} className="fill-forms-checklist__block">
                  <div className="fill-forms-checklist__block-inner">
                    <div className="fill-forms-checklist__block-title-bar">
                      <span className="fill-forms-checklist__block-num" aria-hidden>
                        {circledNumber(idx)}
                      </span>
                      <span className="fill-forms-checklist__block-title">{item.title}</span>
                    </div>
                    <div className="fill-forms-checklist__meta">
                      <span className="fill-forms-checklist__status">
                        {statusLabel(t, item.status)}
                      </span>
                    </div>
                  </div>

                  <div className="fill-forms-checklist__block-action" aria-hidden={!showStart && !showEdit}>
                    {showStart && (
                      <button
                        type="button"
                        className="fill-forms-checklist__block-start"
                        onClick={() => void handleStartOrResume(item)}
                      >
                        {startLabel}
                      </button>
                    )}
                    {showEdit && (
                      <button
                        type="button"
                        className="fill-forms-checklist__block-edit"
                        aria-label={t('submitDocuments.fillFormsChecklist.edit')}
                        onClick={() => void handleEdit(item)}
                      >
                        <img src={editIcon} alt="" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="fill-forms-checklist__footer">
          <ContinueBlock
            line1={t('submitDocuments.fillFormsChecklist.backLine1')}
            buttonLabel={t('submitDocuments.back')}
            onContinue={onBack}
          />
        </div>
      </div>

      {commentForm && (
        <div
          className="fill-forms-checklist__comment-popup"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fill-form-comment-title"
        >
          <div
            className="fill-forms-checklist__comment-backdrop"
            onClick={() => setCommentForm(null)}
            aria-hidden
          />
          <div className="fill-forms-checklist__comment-box">
            <div className="fill-forms-checklist__comment-header">
              <h3 id="fill-form-comment-title" className="fill-forms-checklist__comment-title">
                {commentTitle}
              </h3>
              <button
                type="button"
                className="fill-forms-checklist__comment-close"
                onClick={() => setCommentForm(null)}
                aria-label="閉じる"
              >
                ×
              </button>
            </div>
            <div className="fill-forms-checklist__comment-body">
              <p className="fill-forms-checklist__comment-text">{commentForm.adminComment}</p>
            </div>
            <div className="fill-forms-checklist__comment-footer">
              <button
                type="button"
                className="fill-forms-checklist__comment-ok"
                onClick={() => setCommentForm(null)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FillingFormsChecklistScreen;
